import type { PolicyDocument, PolicySection } from './types'
import { OPERATOR } from './operator'
import {
  CHILDREN,
  DISPOSAL,
  OFFICER,
  OVERSEAS_HEADERS,
  OVERSEAS_REFUSAL,
  PROCESSOR_HEADERS,
  R2_ITEMS_AVATAR,
  R2_RETENTION_AVATAR,
  REMEDIES,
  RETENTION_HEADERS,
  SECURITY,
  changes,
  cloudflareOverseasRow,
  cloudflareRow,
  noAutomaticCollection,
  rights,
  table,
} from './common'

/**
 * retype 개인정보 처리방침.
 *
 * 아홉 문서 중 유일한 **웹 전용**이라 앱 진단 정보(Crashlytics)가 없다 — 공통 행을 쓰지 않는
 * 이유가 이것이다. 대신 이 서비스에만 있는 것이 하나 있다: **학습자가 쓴 코드가 실행된다.**
 * 제출 코드에 개인정보를 넣을 이유는 없지만 넣을 수는 있으므로, 그것이 어디서 실행되고
 * 언제 사라지는지를 명시한다.
 *
 * **2026-08-20 에 그 답이 바뀌었다** (재구축). 네 코스 중 셋은 이제 브라우저 안에서 채점해
 * 코드가 기기를 아예 떠나지 않고, 남은 하나(Spring Boot)만 서버로 간다. 그 대신 그 하나는
 * **큐 테이블에 잠시 저장된다** — 옛 문안의 "데이터베이스나 디스크에 절대 쓰지 않는다"가
 * 더 이상 사실이 아니므로 같은 커밋에서 고쳤다 (docs/privacy-policy.md §사실만 적는다).
 */

const EFFECTIVE_DATE = 'August 15, 2026'
const LAST_REVISED = 'August 20, 2026'

const SECTIONS: readonly PolicySection[] = [
  {
    heading: 'Article 1. Purposes of processing personal data',
    paragraphs: [
      'The Operator processes personal data for the following purposes only. Should a purpose change, the Operator takes the steps required by Article 18 of the Personal Information Protection Act, including obtaining separate consent.',
    ],
    bullets: [
      'Identifying members and keeping them signed in — identifying you through your Kakao account so that your progress belongs to an account',
      'Storing learning progress — recording which lessons you passed, how many attempts you made, and whether you opened the finished solution, so you can pick up where you left off',
      'Grading code you wrote — for the Spring Boot course, running your files in an isolated execution environment to decide whether they pass. For the other three courses this happens inside your own browser and nothing is sent to the Operator',
      'Improving the material — counting where solutions are revealed most often, to find lessons whose explanation is too thin',
    ],
    note: 'Courses and lessons — including the full text, the finished solutions, and the grading rules — can be read and practised without signing in. A login is needed only to keep a record of progress and to have the Spring Boot course graded, because that is the one course whose grading uses the Operator’s own machines.',
  },
  {
    heading: 'Article 2. Personal data processed',
    paragraphs: ['The items below are everything the Operator processes.'],
    bullets: [
      '[Required] Account identifier — the internal identifier issued by the Operator’s authentication server (Keycloak) when you sign in with Kakao',
      '[Collected automatically] Learning progress — whether and when each lesson was passed, how many times a solution was revealed, and when the finished solution was opened',
      '[Processed transiently] Submitted code — for the Spring Boot course only, the contents of the files you wrote in the editor and asked to have graded. Held in a queue until grading finishes and deleted within 24 hours',
    ],
    note: 'Code you write for the Kubernetes, SQL, and React courses is never sent to the Operator; it is graded inside your browser and, unless you clear your browser storage, stays on your own device. No name, email address, phone number, location data, or payment data is collected, and your Kakao profile name is not stored. Neither sensitive data under Article 23 nor unique identifying information under Article 24 of the Personal Information Protection Act is collected. Being a web-only service, it collects no app diagnostics (crash reports).',
  },
  {
    heading: 'Article 3. Where the code you write is run',
    paragraphs: [
      'For the Kubernetes, SQL, and React courses, grading runs entirely inside your browser. The schema validator, the SQLite database, and the React renderer are all downloaded to your device and executed there; the code you write is never transmitted to the Operator. A draft of it is kept in your browser’s local storage so that you can come back to it, and clearing that storage removes it.',
      'The Spring Boot course is the exception, because a Kotlin compiler cannot run in a browser. When you ask for it to be graded, the files are sent to the Operator’s server and placed in a queue, then collected by a grading machine the Operator operates at a separate location in Korea. That machine compiles and tests the files in a container that sits on an internal-only network with no route to the internet or to any other service, holds them only in memory-backed temporary space (tmpfs), and deletes the whole working directory when execution ends.',
      'The queued copy on the server is deleted as soon as grading finishes, and in any case within 24 hours. There is no reason to put personal data into practice code, so we ask that you not write real names or contact details into it.',
    ],
  },
  {
    heading: 'Article 4. Processing and retention periods',
    table: table(RETENTION_HEADERS, [
      [
        'Account identifier and learning progress',
        'Until you ask to withdraw (delete your account) — destroyed on request',
        'Your consent',
      ],
      [
        'Submitted code (Spring Boot course only)',
        'Deleted once grading finishes, and in any case within 24 hours',
        'Your consent',
      ],
    ]),
    note: 'The Operator carries out no transaction that would create a retention duty under the Act on Consumer Protection in Electronic Commerce or similar legislation, so no personal data is kept separately under any statute.',
  },
  {
    heading: 'Article 5. Provision of personal data to third parties',
    paragraphs: [
      'The Operator processes personal data only within the purposes set out in Article 1 and provides it to third parties only with your consent or where Article 17 or Article 18 of the Personal Information Protection Act applies. Personal data is never sold.',
    ],
  },
  {
    heading: 'Article 6. Entrustment of personal data processing',
    paragraphs: ['To provide the service, the Operator entrusts personal data processing as follows.'],
    table: table(PROCESSOR_HEADERS, [
      [
        'Kakao Corp.',
        'Authenticating a Kakao account sign-in',
        'Account identifier',
        'Limited to the moment authentication is processed',
      ],
      cloudflareRow(R2_ITEMS_AVATAR, R2_RETENTION_AVATAR),
    ]),
    note: 'Submitted code is executed on machines the Operator runs itself, both of them in Korea; that work is entrusted to no one. In each processing agreement, as required by Article 26 of the Personal Information Protection Act, the Operator sets out in writing the prohibition on processing beyond the stated purpose, technical and administrative safeguards, restrictions on sub-processing, supervision, and liability for damages, and supervises whether the processor handles personal data safely. If the processors change, this policy is updated and notice given in advance.',
  },
  {
    heading: 'Article 7. Transfer of personal data abroad',
    paragraphs: [
      'Of the processors named in Article 6, Cloudflare, Inc. is located outside Korea, and the entrustment transfers personal data abroad as follows. Kakao Corp. is a Korean company, so no transfer abroad is involved there. Only image files leave Korea: the grading of submitted code, the storage of learning progress, and everything else take place on the Operator’s own machines in Korea.',
    ],
    table: table(OVERSEAS_HEADERS, [cloudflareOverseasRow(R2_ITEMS_AVATAR, R2_RETENTION_AVATAR)]),
    note: OVERSEAS_REFUSAL,
  },
  DISPOSAL,
  rights(
    ['On the site — your progress is visible on the course and lesson screens'],
    'learning progress (whether each lesson was passed, and the number of attempts)',
  ),
  noAutomaticCollection(
    'The login session is kept with an authentication token held in your browser’s local storage rather than a cookie. That token lives only in your own browser, is not used to collect behavioural data, and disappears when you sign out or clear browser storage.',
  ),
  {
    heading: 'Automated decision-making',
    paragraphs: [
      'The Operator makes no automated decision that has a legal effect on you or a comparably significant impact, within the meaning of Article 37-2 of the Personal Information Protection Act. Whether a lesson passes or fails is the result of running the code you submitted through real verification tools — schema validators, compilers, test runners — and it leads to no qualification or assessment beyond your progress record.',
      'The basis for the verdict is disclosed to you in full: every check that failed, and the complete output of the command that was run, are shown on the result screen, so you can see for yourself why something did not pass.',
    ],
  },
  CHILDREN,
  SECURITY,
  OFFICER,
  REMEDIES,
  changes(EFFECTIVE_DATE, [
    'August 26, 2026 — profile photos moved from the Operator’s own server disk to Cloudflare R2 object storage. Articles 6 and 7 were amended accordingly: image files are now held by a processor outside Korea. What is collected, why, and how long it is kept did not change',
    'August 15, 2026 — established and brought into effect (structured on Article 30 of the Personal Information Protection Act)',
    'August 19, 2026 — the site language changed to English and this policy was reissued in English. Nothing about how data is handled changed',
    'August 20, 2026 — the service was rebuilt. Grading for the Kubernetes, SQL, and React courses moved into your own browser, so code written for those courses is no longer sent to the Operator at all. Code for the Spring Boot course is now queued on the server and graded on a separate machine the Operator runs in Korea; that queued copy is deleted once grading finishes and in any case within 24 hours. Articles 1 through 4, 6, and 7 were amended accordingly',
  ]),
]

export const RETYPE_POLICY: PolicyDocument = {
  slug: 'retype',
  title: 'retype Privacy Policy',
  intro: `${OPERATOR}, establishes and publishes the following privacy policy for retype (“the service”) under Article 30 of the Personal Information Protection Act of the Republic of Korea, in order to protect the personal data of its users and to deal promptly and smoothly with any grievance arising from it.`,
  effectiveDate: EFFECTIVE_DATE,
  lastRevisedDate: LAST_REVISED,
  sections: SECTIONS,
}
